// Illaoi Tentacle Smash VFX: a thick organic tentacle rises from below,
// waves sinuously, then slams down onto the target with a green impact shockwave.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const RISE_MS   = 380;
const SLAM_MS   = 160;
const IMPACT_MS = 220;
const FADE_MS   = 200;
const TOTAL = RISE_MS + SLAM_MS + IMPACT_MS + FADE_MS;

const SEGS = 12; // spine segments — more = smoother curve

interface Pt { x: number; y: number }

function buildSpine(
  baseX: number, baseY: number,
  tipX: number,  tipY: number,
  wave: number, waveSeed: number,
  perpX: number, perpY: number,
): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i <= SEGS; i++) {
    const s = i / SEGS;
    // Sinuous offset perpendicular to the spine direction, fading toward the tip
    const sway = Math.sin(s * Math.PI * 2.2 + waveSeed) * wave * (1 - s * 0.6);
    pts.push({
      x: baseX + (tipX - baseX) * s + perpX * sway,
      y: baseY + (tipY - baseY) * s + perpY * sway,
    });
  }
  return pts;
}

function spineNormals(spine: Pt[]): Pt[] {
  return spine.map((_, i) => {
    const a = spine[Math.max(0, i - 1)];
    const b = spine[Math.min(spine.length - 1, i + 1)];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    return { x: -dy / len, y: dx / len };
  });
}

function drawFilledTentacle(
  ctx: CanvasRenderingContext2D,
  spine: Pt[],
  baseWidth: number,
): void {
  const normals = spineNormals(spine);
  const left: Pt[] = [];
  const right: Pt[] = [];

  for (let i = 0; i < spine.length; i++) {
    const s = i / (spine.length - 1);
    const hw = (baseWidth * (1 - s * 0.88)) / 2; // taper from base to tip
    const n = normals[i];
    left.push({ x: spine[i].x + n.x * hw, y: spine[i].y + n.y * hw });
    right.push({ x: spine[i].x - n.x * hw, y: spine[i].y - n.y * hw });
  }

  // Outer dark body
  ctx.beginPath();
  ctx.moveTo(left[0].x, left[0].y);
  for (let i = 1; i < left.length; i++) ctx.lineTo(left[i].x, left[i].y);
  // Rounded tip using a small arc
  const tip = spine[spine.length - 1];
  const tipHw = (baseWidth * (1 - 0.88)) / 2;
  const tipN = normals[normals.length - 1];
  const tipAngle = Math.atan2(tipN.y, tipN.x);
  ctx.arc(tip.x, tip.y, tipHw, tipAngle, tipAngle + Math.PI);
  for (let i = right.length - 1; i >= 0; i--) ctx.lineTo(right[i].x, right[i].y);
  ctx.closePath();

  ctx.fillStyle = "#162b0c";
  ctx.shadowColor = "#20c845";
  ctx.shadowBlur = 14;
  ctx.fill();

  // Inner glow stripe down the center
  ctx.shadowBlur = 20;
  ctx.shadowColor = "#00ff55";
  ctx.strokeStyle = "#2a9040";
  ctx.lineWidth = 2;
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(spine[0].x, spine[0].y);
  for (const p of spine.slice(1)) ctx.lineTo(p.x, p.y);
  ctx.stroke();

  // Rune glow dots at every 3rd joint
  ctx.shadowBlur = 18;
  ctx.shadowColor = "#00ff70";
  ctx.fillStyle = "#80ffaa";
  for (let i = 3; i < spine.length - 1; i += 3) {
    ctx.beginPath();
    ctx.arc(spine[i].x, spine[i].y, 2.2, 0, Math.PI * 2);
    ctx.fill();
  }
}

export class TentacleVFX implements Animation {
  private elapsed = 0;
  private impactFired = false;
  onImpact?: () => void;

  constructor(
    private target: Point,
    private base: Point,
    private radius: number = 20,
  ) {}

  update(dt: number): void {
    const prev = this.elapsed;
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    const impactTime = RISE_MS + SLAM_MS;
    if (!this.impactFired && prev < impactTime && this.elapsed >= impactTime) {
      this.impactFired = true;
      this.onImpact?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed;
    const r = this.radius;

    // Phase progress values
    const riseT   = Math.min(t / RISE_MS, 1);
    const slamT   = t > RISE_MS ? Math.min((t - RISE_MS) / SLAM_MS, 1) : 0;
    const impactT = t > RISE_MS + SLAM_MS
      ? Math.min((t - RISE_MS - SLAM_MS) / IMPACT_MS, 1) : 0;
    const fadeT   = t > RISE_MS + SLAM_MS + IMPACT_MS
      ? (t - RISE_MS - SLAM_MS - IMPACT_MS) / FADE_MS : 0;

    const alpha = 1 - fadeT;

    // Base is the center of the chosen neighbor hex
    const baseX = this.base.x;
    const baseY = this.base.y;

    // Direction from base toward target, normalized
    const dxRaw = this.target.x - baseX;
    const dyRaw = this.target.y - baseY;
    const dist = Math.sqrt(dxRaw * dxRaw + dyRaw * dyRaw) || 1;
    const dirX = dxRaw / dist;
    const dirY = dyRaw / dist;
    // Perpendicular to the spine (for sinuous sway)
    const perpX = -dirY;
    const perpY =  dirX;

    // Peak: slightly past the target (overshoot before slam)
    const peakX = this.target.x + dirX * r * 1.6;
    const peakY = this.target.y + dirY * r * 1.6;

    const riseEased = Easing.easeOutCubic(riseT);
    const riseTipX = baseX + (peakX - baseX) * riseEased;
    const riseTipY = baseY + (peakY - baseY) * riseEased;

    const slamEased = Easing.easeInCubic(slamT);
    const tipX = slamT > 0 ? riseTipX + (this.target.x - riseTipX) * slamEased : riseTipX;
    const tipY = slamT > 0 ? riseTipY + (this.target.y - riseTipY) * slamEased : riseTipY;

    // Wave sways diminish as slam approaches
    const waveAmp = r * 0.55 * (1 - slamEased);
    const waveSeed = t * 0.008;

    const spine = buildSpine(baseX, baseY, tipX, tipY, waveAmp, waveSeed, perpX, perpY);

    ctx.save();
    ctx.globalAlpha = alpha;
    drawFilledTentacle(ctx, spine, r * 0.85);

    // Impact shockwave — appears when slam connects
    if (impactT > 0) {
      const impactAlpha = alpha * (1 - impactT);
      const ringR = r * 1.8 * Easing.easeOutQuad(impactT);

      ctx.globalAlpha = impactAlpha * 0.85;
      ctx.strokeStyle = "#40d060";
      ctx.lineWidth = 3;
      ctx.shadowColor = "#00ff60";
      ctx.shadowBlur = 24;
      ctx.beginPath();
      ctx.arc(this.target.x, this.target.y, ringR, 0, Math.PI * 2);
      ctx.stroke();

      // Second tighter ring
      ctx.globalAlpha = impactAlpha * 0.5;
      ctx.strokeStyle = "#80ffaa";
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = 12;
      ctx.beginPath();
      ctx.arc(this.target.x, this.target.y, ringR * 0.55, 0, Math.PI * 2);
      ctx.stroke();

      // Radial impact streaks
      ctx.globalAlpha = impactAlpha * 0.7;
      ctx.strokeStyle = "#40c060";
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = 10;
      const streaks = 8;
      for (let i = 0; i < streaks; i++) {
        const a = (i / streaks) * Math.PI * 2;
        const inner = r * 0.3;
        const outer = r * 0.3 + ringR * 0.6;
        ctx.beginPath();
        ctx.moveTo(this.target.x + Math.cos(a) * inner, this.target.y + Math.sin(a) * inner);
        ctx.lineTo(this.target.x + Math.cos(a) * outer, this.target.y + Math.sin(a) * outer);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
